// frontend/src/pages/AdminPanel.js
import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header_log';
import '../styles/AdminPanel.css';
import { formatCPF, formatPhoneNumber } from '../utils/formatters';

function AdminPanel() {
  const [students, setStudents] = useState([]);
  const [stats, setStats] = useState({});
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phoneNumber: '',
    cpf: '',
    tags: []
  });
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendModalData, setSendModalData] = useState({ studentId: null, year: new Date().getFullYear() });
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importResults, setImportResults] = useState(null);
  const [importing, setImporting] = useState(false);
  const [showTagSendModal, setShowTagSendModal] = useState(false);
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState('');
  const [sendingByTag, setSendingByTag] = useState(false);
  const [statsYear, setStatsYear] = useState(2026);

  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  // Debounce para o campo de busca
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 1000); // Espera 1 segundo após o usuário parar de digitar

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Carregar estudantes e estatísticas
  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    fetchStudentsAndStats();
  }, [currentUser, navigate, page, debouncedSearchTerm, statsYear]);

  const fetchStudentsAndStats = async () => {
    try {
      setLoading(true);
      const [studentsRes, statsRes] = await Promise.all([
        axios.get(`${baseUrl}/api/students?page=${page}&limit=20&search=${debouncedSearchTerm}`),
        axios.get(`${baseUrl}/api/students/stats?year=${statsYear}`)
      ]);

      setStudents(studentsRes.data.students);
      setPagination(studentsRes.data.pagination);
      setStats(statsRes.data);
      setError(null);
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError('Erro ao carregar os dados dos estudantes.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Erro ao realizar logout:', error);
    }
  };

  const handleAddStudent = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${baseUrl}/api/students/${editingId}`, formData);
        alert('Estudante atualizado com sucesso!');
      } else {
        await axios.post(`${baseUrl}/api/students`, formData);
        alert('Estudante criado com sucesso!');
      }
      setFormData({ name: '', phoneNumber: '', cpf: '', tags: [] });
      setShowForm(false);
      setEditingId(null);
      fetchStudentsAndStats();
    } catch (err) {
      alert('Erro: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleEditStudent = (student) => {
    setFormData({
      name: student.name,
      phoneNumber: student.phoneNumber,
      cpf: student.cpf || '',
      tags: student.tags || []
    });
    setEditingId(student._id);
    setShowForm(true);
  };

  const handleDeleteStudent = async (id) => {
    if (!window.confirm('Tem certeza que deseja deletar este estudante?')) return;
    
    try {
      await axios.delete(`${baseUrl}/api/students/${id}`);
      alert('Estudante deletado com sucesso!');
      fetchStudentsAndStats();
    } catch (err) {
      alert('Erro ao deletar: ' + err.message);
    }
  };

  const handleSendSurvey = (id) => {
    setSendModalData({ studentId: id, year: new Date().getFullYear() });
    setShowSendModal(true);
  };

  const handleConfirmSendSurvey = async () => {
    try {
      await axios.post(`${baseUrl}/api/students/${sendModalData.studentId}/send-survey`, {
        year: sendModalData.year
      });
      alert('Pesquisa enviada com sucesso!');
      setShowSendModal(false);
      fetchStudentsAndStats();
    } catch (err) {
      alert('Erro ao enviar: ' + err.message);
    }
  };

  const handleSelectStudent = (id) => {
    setSelectedStudents(prev =>
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    );
  };

  const handleSendToSelected = () => {
    if (selectedStudents.length === 0) {
      alert('Selecione pelo menos um estudante');
      return;
    }
    
    // Abrir modal para selecionar ano
    setSendModalData({ studentId: null, year: new Date().getFullYear() });
    setShowSendModal(true);
  };

  const handleConfirmSendToMultiple = async () => {
    try {
      await axios.post(`${baseUrl}/api/students/send-survey/batch`, {
        studentIds: selectedStudents,
        year: sendModalData.year
      });
      alert(`Pesquisas enviadas para ${selectedStudents.length} estudantes!`);
      setSelectedStudents([]);
      setShowSendModal(false);
      fetchStudentsAndStats();
    } catch (err) {
      alert('Erro ao enviar: ' + err.message);
    }
  };

  const viewStudentHistory = async (studentId) => {
    try {
      const response = await axios.get(`${baseUrl}/api/students/${studentId}/history`);
      setHistoryData(response.data);
      setShowHistoryModal(true);
    } catch (err) {
      alert('Erro ao carregar histórico: ' + err.message);
    }
  };

  const handleFileChange = (e) => {
    setImportFile(e.target.files[0]);
    setImportResults(null);
  };

  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    const headers = lines[0].split(',').map(h => h.trim());
    
    const students = [];
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      const student = {};
      
      headers.forEach((header, index) => {
        student[header.toLowerCase()] = values[index] || '';
      });
      
      students.push({
        name: student.name || student.nome,
        phoneNumber: student.phonenumber || student.phone || student.telefone,
        cpf: student.cpf || ''
      });
    }
    
    return students;
  };

  const handleImportCSV = async () => {
    if (!importFile) {
      alert('Selecione um arquivo CSV');
      return;
    }

    setImporting(true);
    
    try {
      const text = await importFile.text();
      const students = parseCSV(text);
      
      const response = await axios.post(`${baseUrl}/api/students/import-csv`, { students });
      
      setImportResults(response.data);
      setImportFile(null);
      fetchStudentsAndStats();
    } catch (err) {
      alert('Erro ao importar: ' + (err.response?.data?.error || err.message));
    } finally {
      setImporting(false);
    }
  };

  // Buscar tags disponíveis
  const fetchTags = async () => {
    try {
      console.log('Buscando tags em:', `${baseUrl}/api/students/tags`);
      const response = await axios.get(`${baseUrl}/api/students/tags`);
      console.log('Tags recebidas:', response.data);
      setAvailableTags(response.data.tags);
    } catch (err) {
      console.error('Erro ao buscar tags:', err);
      console.error('Detalhes do erro:', err.response?.data);
    }
  };

  // Abrir modal de envio por tag
  const handleOpenTagSendModal = () => {
    fetchTags();
    setShowTagSendModal(true);
  };

  // Enviar pesquisa por tag
  const handleSendByTag = async () => {
    if (!selectedTag) {
      alert('Selecione uma tag');
      return;
    }

    if (!window.confirm(`Deseja enviar a pesquisa para todos os alunos com a tag "${selectedTag}"?`)) {
      return;
    }

    try {
      setSendingByTag(true);
      const response = await axios.post(`${baseUrl}/api/students/send-survey/by-tag`, {
        tag: selectedTag,
        year: new Date().getFullYear()
      });

      // Novo fluxo assíncrono: backend retorna jobId/total/status
      if (response.data?.jobId) {
        const { jobId, total, status } = response.data;
        alert(`🚀 Envio iniciado!\n\nJob: ${jobId}\nTotal para envio: ${total}\nStatus inicial: ${status || 'queued'}\n\nAcompanhe o progresso na tela ou consulte o status novamente.`);

        // Opcional: buscar status atual após disparo
        try {
          const statusResp = await axios.get(`${baseUrl}/api/students/send-survey/by-tag/status/${jobId}`);
          const job = statusResp.data;
          alert(`📊 Status do job\n\nTotal: ${job.total}\nEnviados: ${job.sent.length}\nFalhas: ${job.failed.length}\nStatus: ${job.status}`);
        } catch (statusErr) {
          console.error('Erro ao consultar status do job:', statusErr);
        }
      } else if (response.data?.results) {
        // Compatibilidade com respostas antigas
        const { results } = response.data;
        alert(`✅ Envio concluído!\n\nTotal: ${results.total}\nEnviados: ${results.sent.length}\nFalhas: ${results.failed.length}`);
      } else {
        alert('Envio iniciado.');
      }

      setShowTagSendModal(false);
      setSelectedTag('');
      fetchStudentsAndStats();
    } catch (err) {
      alert('Erro ao enviar: ' + (err.response?.data?.error || err.message));
    } finally {
      setSendingByTag(false);
    }
  };

  // Função para gerar array de páginas com "..."
  const getPageNumbers = () => {
    const totalPages = pagination.pages;
    const currentPage = page;
    const delta = 2; // Quantas páginas mostrar ao redor da página atual
    
    if (totalPages <= 7) {
      // Se tem 7 ou menos páginas, mostra todas
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    
    const pages = [];
    
    // Sempre mostra primeira página
    pages.push(1);
    
    // Calcula o range ao redor da página atual
    const startPage = Math.max(2, currentPage - delta);
    const endPage = Math.min(totalPages - 1, currentPage + delta);
    
    // Adiciona "..." depois da primeira página se necessário
    if (startPage > 2) {
      pages.push('...');
    }
    
    // Adiciona páginas do meio
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    // Adiciona "..." antes da última página se necessário
    if (endPage < totalPages - 1) {
      pages.push('...');
    }
    
    // Sempre mostra última página
    if (totalPages > 1) {
      pages.push(totalPages);
    }
    
    return pages;
  };

  // Função para baixar modelo CSV
  const handleDownloadTemplate = () => {
    const csvContent = 'name,phoneNumber,cpf,tags\nJoão Silva,5561999999999,123.456.789-00,2025\nMaria Santos,5561988888888,987.654.321-00,"2025,Turma A"';
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', 'modelo_importacao_estudantes.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) return <div className="loading">Carregando...</div>;

  return (
    <div className="admin-container">
      <div className="no-print">
        <Header
          selectedUnit="Painel Administrativo"
          handleUnitChange={null}
          showUnitSelector={false}
          handleLogout={handleLogout}
        />
      </div>

      <div className="admin-content">
        {/* Seção de Estatísticas */}
        <div className="stats-section">
          <div className="stats-header">
            <h2>Estatísticas</h2>
            <div className="stats-year-filter">
              <label htmlFor="stats-year">Ano:</label>
              <select
                id="stats-year"
                value={statsYear}
                onChange={(e) => setStatsYear(parseInt(e.target.value))}
                className="form-control"
                style={{ width: '120px' }}
              >
                {[2024, 2025, 2026, 2027].map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="stats-grid">
            <div className="stat-card">
              <h3>Total de Estudantes</h3>
              <p className="stat-value">{stats.total || 0}</p>
            </div>
            <div className="stat-card">
              <h3>Pendentes</h3>
              <p className="stat-value">{stats.notResponded || 0}</p>
            </div>
            <div className="stat-card">
              <h3>Respondidas</h3>
              <p className="stat-value">{stats.responded || 0}</p>
            </div>
          </div>
        </div>

        {/* Seção de Ações */}
        <div className="actions-section">
          <button
            className="btn btn-primary"
            onClick={() => {
              setShowForm(!showForm);
              setEditingId(null);
              setFormData({ name: '', phoneNumber: '', cpf: '', tags: [] });
            }}
          >
            {showForm ? '✕ Cancelar' : '+ Novo Estudante'}
          </button>
          
          <button
            className="btn btn-info"
            onClick={() => setShowImportModal(true)}
          >
            📥 Importar CSV
          </button>

          <button
            className="btn btn-success"
            onClick={handleOpenTagSendModal}
          >
            🏷️ Enviar por Tag
          </button>

          {selectedStudents.length > 0 && (
            <button
              className="btn btn-success"
              onClick={handleSendToSelected}
            >
              Enviar Pesquisa ({selectedStudents.length})
            </button>
          )}
        </div>

        {/* Formulário de Adicionar/Editar */}
        {showForm && (
          <div className="form-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3>{editingId ? 'Editar Estudante' : 'Adicionar Novo Estudante'}</h3>
              <button 
                onClick={() => {
                  setShowForm(false);
                  setEditingId(null);
                  setFormData({ name: '', phoneNumber: '', cpf: '', tags: [] });
                }}
                className="btn btn-secondary"
                style={{ marginBottom: '10px' }}
              >
                ✕ Fechar
              </button>
            </div>
            <form onSubmit={handleAddStudent}>
              <div className="form-group">
                <label>Nome:</label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Telefone:</label>
                <input
                  type="text"
                  value={formData.phoneNumber || ''}
                  onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                  placeholder="5561999999999"
                  required
                />
              </div>
              <div className="form-group">
                <label>CPF (opcional):</label>
                <input
                  type="text"
                  value={formData.cpf || ''}
                  onChange={(e) => setFormData({ ...formData, cpf: e.target.value })}
                  placeholder="000.000.000-00"
                />
              </div>
              <div className="form-group">
                <label>Tags (separadas por vírgula):</label>
                <input
                  type="text"
                  value={Array.isArray(formData.tags) ? formData.tags.join(', ') : (formData.tags || '')}
                  onChange={(e) => {
                    const tagsArray = e.target.value.split(',').map(t => t.trim()).filter(t => t);
                    setFormData({ ...formData, tags: tagsArray });
                  }}
                  placeholder="2025, Turma A, etc"
                />
              </div>
              <button type="submit" className="btn btn-success">
                {editingId ? 'Atualizar' : 'Adicionar'}
              </button>
            </form>
          </div>
        )}

        {/* Seção de Busca */}
        <div className="search-section">
          <input
            type="text"
            placeholder="Buscar por nome, telefone ou CPF..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
            className="search-input"
          />
        </div>

        {/* Tabela de Estudantes */}
        <div className="table-section">
          <h2>Estudantes</h2>
          {error && <div className="error-message">{error}</div>}
          
          <div className="table-wrapper">
            <table className="students-table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={selectedStudents.length === students.length && students.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedStudents(students.map(s => s._id));
                        } else {
                          setSelectedStudents([]);
                        }
                      }}
                    />
                  </th>
                  <th>Nome</th>
                  <th>Telefone</th>
                  <th>CPF</th>
                  <th>Tags</th>
                  <th>Status da Mensagem</th>
                  <th>Anos Enviados</th>
                  <th>Histórico</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {students.map((student) => (
                  <tr key={student._id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedStudents.includes(student._id)}
                        onChange={() => handleSelectStudent(student._id)}
                      />
                    </td>
                    <td>{student.name}</td>
                    <td>{formatPhoneNumber(student.phoneNumber)}</td>
                    <td>{formatCPF(student.cpf)}</td>
                    <td>
                      <div className="tags-container">
                        {student.tags && student.tags.length > 0 ? (
                          student.tags.map((tag, idx) => (
                            <span key={idx} className="tag-badge">{tag}</span>
                          ))
                        ) : (
                          <span>-</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className={`status ${student.messageStatus.toLowerCase().replace(' ', '-')}`}>
                        {student.messageStatus}
                      </span>
                    </td>
                    <td>
                      {student.messageSentYears && student.messageSentYears.length > 0 
                        ? student.messageSentYears.join(', ')
                        : '-'
                      }
                    </td>
                    <td>
                      {student.surveyHistory && student.surveyHistory.length > 0 ? (
                        <div className="history-summary">
                          <span>
                            {student.surveyHistory.filter(h => h.responded).length}/{student.surveyHistory.length} respondidas
                          </span>
                          <button 
                            className="btn-link"
                            onClick={() => viewStudentHistory(student._id)}
                            style={{marginLeft: '8px', fontSize: '12px'}}
                          >
                            Ver detalhes
                          </button>
                        </div>
                      ) : (
                        <span>-</span>
                      )}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn btn-sm btn-info"
                          onClick={() => handleSendSurvey(student._id)}
                        >
                          Enviar
                        </button>
                        <button
                          className="btn btn-sm btn-warning"
                          onClick={() => handleEditStudent(student)}
                        >
                          Editar
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDeleteStudent(student._id)}
                        >
                          Deletar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Paginação */}
          {pagination.pages > 1 && (
            <div className="pagination">
              <button
                className="page-btn"
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
              >
                ← Anterior
              </button>
              
              {getPageNumbers().map((p, index) => (
                p === '...' ? (
                  <span key={`ellipsis-${index}`} className="page-ellipsis">...</span>
                ) : (
                  <button
                    key={p}
                    className={`page-btn ${page === p ? 'active' : ''}`}
                    onClick={() => setPage(p)}
                  >
                    {p}
                  </button>
                )
              ))}
              
              <button
                className="page-btn"
                onClick={() => setPage(Math.min(pagination.pages, page + 1))}
                disabled={page === pagination.pages}
              >
                Próximo →
              </button>
            </div>
          )}
        </div>

        {/* Modal de Envio de Pesquisa */}
        {showSendModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h3>{sendModalData.studentId ? 'Enviar Pesquisa' : `Enviar para ${selectedStudents.length} estudantes`}</h3>
              <div className="form-group">
                <label htmlFor="year">Selecionar Ano:</label>
                <select
                  id="year"
                  value={sendModalData.year}
                  onChange={(e) => setSendModalData({
                    ...sendModalData,
                    year: parseInt(e.target.value)
                  })}
                  className="form-control"
                >
                  {[2024, 2025, 2026, 2027].map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
              <div className="modal-actions">
                <button
                  className="btn btn-primary"
                  onClick={sendModalData.studentId ? handleConfirmSendSurvey : handleConfirmSendToMultiple}
                >
                  Confirmar
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowSendModal(false)}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Histórico */}
        {showHistoryModal && historyData && (
          <div className="modal-overlay" onClick={() => setShowHistoryModal(false)}>
            <div className="modal-content history-modal" onClick={(e) => e.stopPropagation()}>
              <h3>Histórico de Envios - {historyData.name}</h3>
              <p>Total enviado: {historyData.totalSent} | Respondidas: {historyData.totalResponded}</p>
              
              <div className="history-list">
                {historyData.history.length > 0 ? (
                  <table className="history-table">
                    <thead>
                      <tr>
                        <th>Data/Hora</th>
                        <th>Mês/Ano</th>
                        <th>Status</th>
                        <th>Respondeu</th>
                        <th>Data Resposta</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historyData.history.map((entry, index) => (
                        <tr key={index}>
                          <td>{new Date(entry.sentAt).toLocaleString('pt-BR')}</td>
                          <td>{entry.monthName}/{entry.year}</td>
                          <td>
                            <span className={`status ${entry.status.toLowerCase()}`}>
                              {entry.status}
                            </span>
                          </td>
                          <td>
                            <span className={entry.responded ? 'yes' : 'no'}>
                              {entry.responded ? 'Sim' : 'Não'}
                            </span>
                          </td>
                          <td>
                            {entry.respondedAt 
                              ? new Date(entry.respondedAt).toLocaleString('pt-BR')
                              : '-'
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p>Nenhum histórico disponível</p>
                )}
              </div>
              
              <div className="modal-actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowHistoryModal(false)}
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Importação CSV */}
        {showImportModal && (
          <div className="modal-overlay" onClick={() => {
            setShowImportModal(false);
            setImportResults(null);
            setImportFile(null);
          }}>
            <div className="modal-content import-modal" onClick={(e) => e.stopPropagation()}>
              <h3>Importar Estudantes via CSV</h3>
              
              <div className="import-instructions">
                <p><strong>Formato do CSV:</strong></p>
                <p>O arquivo CSV deve ter as colunas: <code>name</code>, <code>phoneNumber</code>, <code>cpf</code></p>
                
                <div style={{background: '#e6f7ff', padding: '10px', borderRadius: '5px', margin: '10px 0', fontSize: '13px'}}>
                  <strong>💡 Dica:</strong> O sistema normaliza automaticamente:
                  <ul style={{marginTop: '5px', marginBottom: '5px'}}>
                    <li>CPF: Remove pontos e traços (aceita <code>123.456.789-00</code> ou <code>12345678900</code>)</li>
                    <li>Telefone: Adiciona "55" se não tiver (aceita <code>61999999999</code> ou <code>5561999999999</code>)</li>
                  </ul>
                </div>
                
                <button 
                  onClick={handleDownloadTemplate}
                  className="btn btn-sm btn-outline"
                  style={{marginTop: '10px', marginBottom: '10px'}}
                >
                  📥 Baixar modelo CSV
                </button>
                
                <p>Exemplo:</p>
                <pre>
name,phoneNumber,cpf,tags{'\n'}
João Silva,5561999999999,123.456.789-00,2025{'\n'}
Maria Santos,5561988888888,987.654.321-00,"2025,Turma A"
                </pre>
                <p><strong>O sistema irá:</strong></p>
                <ul>
                  <li>✅ Criar novos estudantes que não existem</li>
                  <li>🔄 Atualizar dados (nome/telefone) se o CPF já existir</li>
                  <li>🏷️ Tags podem ser separadas por vírgula (ex: "2025,Turma A")</li>
                  <li>🔄 Tags de estudantes existentes serão mescladas (sem duplicatas)</li>
                  <li>⏭️ Pular registros duplicados ou com erros</li>
                </ul>
              </div>

              <div className="form-group">
                <label htmlFor="csv-file">Selecionar arquivo CSV:</label>
                <input
                  id="csv-file"
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  className="form-control"
                />
              </div>

              {importResults && (
                <div className="import-results">
                  <h4>Resultados da Importação</h4>
                  <div className="results-summary">
                    <p>📊 Total processado: <strong>{importResults.total}</strong></p>
                    <p>✅ Criados: <strong>{importResults.created}</strong></p>
                    <p>🔄 Atualizados: <strong>{importResults.updated}</strong></p>
                    <p>⏭️ Ignorados: <strong>{importResults.skipped}</strong></p>
                    <p>❌ Erros: <strong>{importResults.errors}</strong></p>
                  </div>

                  {importResults.details.errors.length > 0 && (
                    <div className="import-errors">
                      <h5>Erros encontrados:</h5>
                      <ul>
                        {importResults.details.errors.map((err, idx) => (
                          <li key={idx}>
                            <strong>{err.data.name || 'Sem nome'}:</strong> {err.error}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              <div className="modal-actions">
                <button
                  className="btn btn-primary"
                  onClick={handleImportCSV}
                  disabled={!importFile || importing}
                >
                  {importing ? 'Importando...' : 'Importar'}
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowImportModal(false);
                    setImportResults(null);
                    setImportFile(null);
                  }}
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Envio por Tag */}
        {showTagSendModal && (
          <div className="modal-overlay" onClick={() => setShowTagSendModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>📤 Enviar Pesquisa por Tag</h2>
              
              <div className="form-group">
                <label>Selecione a tag:</label>
                <select 
                  value={selectedTag} 
                  onChange={(e) => setSelectedTag(e.target.value)}
                  className="form-control"
                >
                  <option value="">-- Selecione uma tag --</option>
                  {availableTags.map((tagInfo) => (
                    <option key={tagInfo.tag} value={tagInfo.tag}>
                      {tagInfo.tag} ({tagInfo.count} alunos)
                    </option>
                  ))}
                </select>
              </div>

              {selectedTag && (
                <div className="tag-info">
                  <p>
                    ⚠️ A pesquisa será enviada para <strong>
                      {availableTags.find(t => t.tag === selectedTag)?.count || 0}
                    </strong> alunos com a tag <strong>"{selectedTag}"</strong>
                  </p>
                </div>
              )}

              <div className="modal-actions">
                <button
                  className="btn btn-success"
                  onClick={handleSendByTag}
                  disabled={!selectedTag || sendingByTag}
                >
                  {sendingByTag ? 'Enviando...' : '📤 Enviar'}
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowTagSendModal(false);
                    setSelectedTag('');
                  }}
                  disabled={sendingByTag}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminPanel;
